import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from '@headlessui/react'
import React, { Fragment } from 'react'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { twJoin, twMerge } from 'tailwind-merge'
import { useToggle } from 'shared/hooks/use-toggle'
import useUser, { isDomainOwner, isWsAdmin, isWsAssistant } from 'shared/hooks/use-user'
import ChevronUpIcon from 'shared/icons/chevron-up-icon'
import HomeIcon from 'shared/icons/home-icon'
import MembersIcon from 'shared/icons/members-icon'
import PlusIcon from 'shared/icons/plus-icon'
import PostsIcon from 'shared/icons/posts-icon'
import { ScheduledPostsIcon } from 'shared/icons/scheduled-posts-icon'
import SettingsIcon from 'shared/icons/settings-icon'
import AddTopic from 'modules/community/components/add-topic'
import { CommunitySearchForm } from 'modules/community/components/search-form'
import { CommunitySearchFormTrigger } from 'modules/community/components/search-form/trigger'
import { TopicList } from 'modules/community/components/topic/components/topic-list'
import { useCommunityWithTopics } from 'modules/community/hooks/use-community-with-topics'
import { hasPermission } from 'modules/community/utils/abac-roles-permissions'

const getOptionClassName =
  'cursor-pointer overflow-hidden text-ellipsis px-5 py-1.5 gap-x-2 text-text-primary/60 data-[selected]:text-brand-primary'

function MobileNav() {
  const { t } = useTranslation()
  const { user } = useUser()
  const { data: community } = useCommunityWithTopics()
  const router = useRouter()
  const { topicPath, communityPath, q } = router.query

  const canViewScheduledPosts = hasPermission(user, 'scheduledPosts', 'schedule', undefined)
  const [isSearchOpen, toggleSearch] = useToggle(!!q)

  if (!community || !user) {
    return (
      <div className="flex animate-pulse justify-between px-5 py-2">
        <div className="h-6 w-10/12 rounded-lg bg-skeleton-primary" />
        <div className="h-6 w-1/12 rounded-lg bg-skeleton-primary" />
      </div>
    )
  }

  const isHomePage = router.asPath === `/community/${communityPath}`
  const selectedTopic = community.topics.find(topic => topic.path === topicPath)
  const isSettingsPage = router.pathname.includes('settings')
  const isMembersPage = router.pathname.includes('members')
  const isPendingPostsPage = router.pathname.includes('pending-posts')
  const isScheduledPostsPage = router.pathname.includes('scheduled-posts')

  function switchTopic(topicPath: string) {
    if (topicPath === 'home') {
      router.push(`/community/${communityPath}`)
    } else {
      router.push(`/community/${communityPath}/${topicPath}`)
    }
  }

  const menuItems = [
    {
      href: `/community/${communityPath}`,
      icon: <HomeIcon />,
      isActive: isHomePage || selectedTopic,
      show: true,
    },
    {
      href: `/community/${communityPath}/members`,
      icon: <MembersIcon />,
      isActive: isMembersPage,
      show: true,
    },
    {
      href: `/community/${communityPath}/settings`,
      icon: <SettingsIcon />,
      isActive: isSettingsPage,
      show: isDomainOwner(user) || isWsAdmin(user),
    },
    {
      href: `/community/${communityPath}/pending-posts`,
      icon: <PostsIcon />,
      isActive: isPendingPostsPage,
      show: (isDomainOwner(user) || isWsAdmin(user)) && !community.autoPostsApproval,
    },
    {
      href: `/community/${communityPath}/scheduled-posts`,
      icon: <ScheduledPostsIcon />,
      isActive: isScheduledPostsPage,
      show: canViewScheduledPosts,
    },
  ].filter(item => item.show)

  return (
    <div className="sticky top-0 z-20 flex w-full flex-col lg:hidden">
      <div className="flex w-full">
        {menuItems.map(({ href, icon, isActive }) => (
          <Link
            href={href}
            key={href}
            className={twJoin(
              'flex flex-1 justify-center border-b-[3px] bg-background-secondary py-4',
              isActive
                ? 'border-brand-primary text-brand-primary'
                : 'border-text-secondary/30 text-text-secondary',
            )}
          >
            {icon}
          </Link>
        ))}
      </div>
      <div className="flex flex-row items-center gap-x-4 border-b border-b-text-secondary/30 bg-background-secondary px-5 py-3">
        <Listbox value={selectedTopic?.path || 'home'} onChange={switchTopic}>
          <div className={'flex min-w-0 flex-1 items-center py-3'}>
            <ListboxButton className="group/listbox-button relative flex w-full cursor-default items-center gap-x-1.5 rounded-lg text-left font-semibold text-text-secondary focus:outline-none focus-visible:ring-2 focus-visible:ring-background-secondary/75 focus-visible:ring-offset-2 sm:text-sm">
              <span className="line-clamp-2 block overflow-hidden text-ellipsis">
                {selectedTopic ? selectedTopic.name : t(`mobile_nav.home`)}
              </span>
              <ChevronUpIcon className="size-6 shrink-0 transition-transform duration-200 group-data-[active]/listbox-button:rotate-180" />
            </ListboxButton>
          </div>

          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <ListboxOptions
              anchor="bottom"
              className="absolute z-10 mt-[25px] w-full !overflow-visible"
            >
              <div className="max-h-60 overflow-auto bg-background-secondary py-3 text-base shadow-lg focus:outline-none">
                <ListboxOption key="home" value="home" className={getOptionClassName}>
                  {t(`mobile_nav.home`)}
                </ListboxOption>

                <TopicList topicItemClassName={getOptionClassName} />
              </div>
            </ListboxOptions>
          </Transition>
        </Listbox>
        {(isDomainOwner(user) || isWsAdmin(user) || isWsAssistant(user)) && (
          <div className={'flex items-center gap-2 text-text-secondary'}>
            <AddTopic className="text-text-secondary" icon={PlusIcon} />
          </div>
        )}
        <CommunitySearchFormTrigger className="justify-self-end" onOpen={toggleSearch} />
      </div>
      {isSearchOpen && (
        <CommunitySearchForm className="rounded-none border-b border-b-text-secondary/30" />
      )}
    </div>
  )
}

export default MobileNav
